import { config } from '../../../config/config';
import { getHasuraClaimsFromToken, getStoredIdToken } from '../../../authToken';
import { getFreshIdToken } from '../../../utils/freshIdToken';
import { ADMIN_PORTAL_ALLOWED } from '../../../helper';
import type { VlSupportEmailType } from '../vlSupportEmail';

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');
const NODE_API_URL = trimTrailingSlash(
  process.env.REACT_APP_NODE_API_URL || config.node_api_url || '',
);

/** Group/split can take a long time on Victory Live — do not abort the browser request. */
const fetchNoTimeout = async (url: string, init: RequestInit): Promise<Response> => {
  try {
    return await fetch(url, init);
  } catch (err: any) {
    const msg = String(err?.message || err);
    if (/timeout of \d+ms exceeded/i.test(msg) || err?.name === 'AbortError') {
      throw new Error(
        'Victory Live result is UNKNOWN for Split/Group. Do not retry yet — wait, then use Sync from VL to confirm whether it was processed.',
      );
    }
    throw err;
  }
};

const resolveHasuraRoleHeader = (token: string): string => {
  const claims = getHasuraClaimsFromToken(token);
  if (!claims) return '';

  const allowedRoles = Array.isArray(claims?.['x-hasura-allowed-roles'])
    ? claims['x-hasura-allowed-roles']
    : [];
  const defaultRole = claims?.['x-hasura-default-role'] || '';
  const requestedRole = localStorage.getItem('x-hasura-user-role') || '';

  if (
    requestedRole &&
    ADMIN_PORTAL_ALLOWED.includes(requestedRole) &&
    allowedRoles.includes(requestedRole)
  ) {
    return requestedRole;
  }

  if (defaultRole && allowedRoles.includes(defaultRole)) {
    return defaultRole;
  }

  return '';
};

const authHeaders = (tokenOverride?: string): Record<string, string> => {
  const token = tokenOverride ?? getStoredIdToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    const hasuraRole = resolveHasuraRoleHeader(token);
    if (hasuraRole) headers['x-hasura-role'] = hasuraRole;
  }
  return headers;
};

export type SubmitListingsToVlResult = {
  successCount: number;
  failedCount: number;
  skippedCount?: number;
  unknownCount?: number;
  results?: {
    tpId: string;
    status: 'success' | 'failed' | 'skipped' | 'unknown';
    message?: string;
    code?: string;
    productionMismatch?: ProductionMismatchDetails;
    reconId?: string;
    vlTicketGroupId?: string | number | null;
    vlListingId?: string | number | null;
    vlItemId?: string | number | null;
    correlationId?: string;
  }[];
  message?: string;
  code?: string;
  details?: ProductionMismatchDetails;
};

export type ProductionMismatchDetails = {
  code: 'VL_PRODUCTION_MISMATCH';
  eventId: string;
  eventName: string;
  venue: string;
  eventDate: string;
  reason: string;
  queriesTried: string[];
  candidates: Array<{
    productionId: string;
    eventName: string;
    venueName: string;
    eventDate: string;
    score: number;
  }>;
};

export type VlProductionSearchResult = {
  query: string;
  eventName?: string;
  venue?: string;
  eventDate?: string;
  /** Present when TM status is Date TBD — search window start (today). */
  eventDateStart?: string | null;
  /** Present when TM status is Date TBD — search window end (month +6 months). */
  eventDateEnd?: string | null;
  queriesTried?: string[];
  dateWindowDays?: number | null;
  /** Present when TM status is Date TBD (months ahead used for the search window). */
  dateTbdMonths?: number | null;
  suggestedProductionId?: string | null;
  productions: Array<{
    productionId: string;
    eventName: string;
    venueName: string;
    eventDate: string;
    eventTime?: string;
    stubhubId?: string | null;
    seatgeekId?: string | null;
    score: number;
    acceptable?: boolean;
    dateDist?: number;
    isSuggested?: boolean;
  }>;
};

export type MapVlProductionResult = {
  eventId: string;
  productionId: string;
  verified: boolean;
  message?: string;
  production?: {
    productionId: string;
    eventName: string | null;
    venueName: string | null;
    eventDate: string | null;
  };
};

export type VerifyTicketPlacementsResult = {
  message: string;
  updatedIds: string[];
  status: 'Verified';
};

export type ConfirmVlProductionResult = {
  eventId: string;
  /** Present when overwrite forked selected listings onto a new event. */
  previousEventId?: string;
  /** True when selected listings were moved to a new event so siblings keep their PID. */
  forked?: boolean;
  productionId: string;
  /** True when only the event PID was written — status unchanged, no VL inventory. */
  savePidOnly?: boolean;
  /** Set only after PID is saved with status promotion — Verified with PID Confirmed. */
  status?: 'Verified';
  confirmationMessage: 'PID Confirmed' | 'PID Saved';
  confirmedAt: string;
  ticketPlacementIds: string[];
  message?: string;
  production?: {
    productionId: string;
    eventName: string | null;
    venueName: string | null;
    eventDate: string | null;
    stubhubId?: string | null;
    seatgeekId?: string | null;
  };
};

export type SyncVlListingStatusResult = {
  successCount: number;
  failedCount: number;
  skippedCount?: number;
  unknownCount?: number;
  unchangedCount?: number;
  results?: {
    tpId: string;
    status: 'success' | 'failed' | 'skipped' | 'unchanged' | 'unknown';
    message?: string;
    previousStatus?: string | null;
    newStatus?: string | null;
    vlItemId?: string | null;
    vlListingId?: string | null;
    barcode?: string | null;
    barcodeStatus?: string | null;
    section?: string | null;
    row?: string | null;
    seat?: string | number | null;
    onHold?: boolean;
    shared?: boolean;
    vlShare?: boolean;
    enabledMarketplaceIds?: string[];
    vlMarketplaces?: string[];
    eventName?: string | null;
    venue?: string | null;
    productionId?: string | null;
    listPrice?: number | null;
    internalNotes?: string | null;
    externalNotes?: string | null;
  }[];
  message?: string;
};

export async function submitListingsToVl(
  ticketPlacementIds: string[],
  options?: {
    intakeMode?: 'csv' | 'purchaseOrder' | 'createInventory';
    seatLevelNotesByTpId?: Record<string, { internalNote: string; externalNote: string }>;
    defaultListPrice?: number;
    preserveStatus?: boolean;
    clearWrongPid?: boolean;
  },
): Promise<SubmitListingsToVlResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/submitListingsToVl`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        ticketPlacementIds,
        ...(options?.intakeMode ? { intakeMode: options.intakeMode } : {}),
        ...(options?.seatLevelNotesByTpId ? { seatLevelNotesByTpId: options.seatLevelNotesByTpId } : {}),
        ...(options?.defaultListPrice != null ? { defaultListPrice: options.defaultListPrice } : {}),
        ...(options?.preserveStatus ? { preserveStatus: true } : {}),
        ...(options?.clearWrongPid ? { clearWrongPid: true } : {}),
      },
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to submit listings to Victory Live';
    throw new Error(message);
  }
  return responseData as SubmitListingsToVlResult;
}

export async function syncVlListingStatus(
  ticketPlacementIds: string[],
  options?: { syncScope?: string | string[] | null },
): Promise<SyncVlListingStatusResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/syncVlListingStatus`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        ticketPlacementIds,
        ...(options?.syncScope ? { syncScope: options.syncScope } : {}),
      },
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to sync Victory Live status';
    throw new Error(message);
  }
  return responseData as SyncVlListingStatusResult;
}

export type StartVlAccountCrawlResult = {
  success: boolean;
  accountsSource?: 'request' | 'config' | 'accounts_search' | 'seasons' | 'mixed';
  accountIds?: string[];
  accountId?: string;
  statusCode?: number;
  successCount?: number;
  failedCount?: number;
  message?: string;
  seasonsNotes?: string[];
  response?: any;
  results?: Array<{
    accountId: string;
    success: boolean;
    statusCode: number;
    message?: string;
    username?: string | null;
    marketplaceName?: string | null;
    marketplaceId?: string | null;
    crawlMethod?: 'purchase' | 'seasons';
  }>;
};

export type VlAccountCrawlStatusResult = {
  complete: boolean;
  processingCount: number;
  completedCount: number;
  failedCount: number;
  total: number;
  results: Array<{
    accountId: string;
    status: 'processing' | 'completed' | 'failed';
    crawlStatus: string | null;
    lastCrawlTimestamp: string | null;
    lastSuccessfulCrawlTimestamp: string | null;
    lastErrorMessage: string | null;
    message?: string;
  }>;
};

export type BarcodeSyncRecommendResult = {
  mode: string;
  eventName?: string | null;
  venue?: string | null;
  league?: string | null;
  primaryTeam?: string | null;
  isTeamEvent?: boolean;
  sheetPlatform?: string | null;
  sheetType?: string | null;
  seasonsAccount?: {
    accountId: string;
    username?: string | null;
    marketplaceName?: string | null;
    marketplaceId?: string | null;
    teamName: string;
    recommended: boolean;
  } | null;
  purchaseAccounts: Array<{
    accountId: string;
    username?: string | null;
    marketplaceName: string;
    marketplaceId?: string | null;
    managerName: string;
    recommended: boolean;
    available?: boolean;
  }>;
  /** Extra configured Purchase accounts that are not one of the 5 managers (e.g. preview Generic Purchase). */
  otherPurchaseAccounts?: Array<{
    accountId: string;
    username?: string | null;
    marketplaceName: string;
    managerName: string;
    recommended: boolean;
    available?: boolean;
  }>;
  seasonsNotes?: string[];
  reason?: string;
  rulesNote?: string;
};

export async function startVlAccountCrawl(options?: {
  accountId?: string;
  accountIds?: string[];
  teamName?: string;
  ticketPlacementIds?: string[];
}): Promise<StartVlAccountCrawlResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/vl/startAccountCrawl`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        ...(options?.accountId ? { accountId: options.accountId } : {}),
        ...(options?.accountIds?.length ? { accountIds: options.accountIds } : {}),
        ...(options?.teamName ? { teamName: options.teamName } : {}),
        ...(options?.ticketPlacementIds?.length
          ? { ticketPlacementIds: options.ticketPlacementIds }
          : {}),
      },
    }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok || responseData?.success === false) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to start Victory Live account crawl';
    throw new Error(message);
  }
  return responseData as StartVlAccountCrawlResult;
}

export async function getVlAccountCrawlStatus(
  accountIds: string[],
): Promise<VlAccountCrawlStatusResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/vl/getAccountCrawlStatus`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        accountIds,
      },
    }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to get account crawl status';
    throw new Error(message);
  }
  return responseData as VlAccountCrawlStatusResult;
}

export async function recommendBarcodeSyncAccounts(input: {
  eventName?: string;
  venue?: string;
  league?: string;
  teamName?: string;
  eventId?: string;
  homeTeam?: string;
}): Promise<BarcodeSyncRecommendResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/vl/recommendBarcodeSyncAccounts`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to recommend barcode sync accounts';
    throw new Error(message);
  }
  return responseData as BarcodeSyncRecommendResult;
}

export type CheckBarcodesSeat = {
  tpId: string;
  userName?: string | null;
  section?: string | null;
  row?: string | null;
  seat?: string | number | null;
  barcode?: string | null;
  barcodeAttached: boolean;
  status: string;
  accountIdentifier?: string | null;
  eventName?: string | null;
};

export type CheckBarcodesResult = {
  eventName?: string | null;
  total: number;
  attachedCount: number;
  missingCount: number;
  seats: CheckBarcodesSeat[];
};

export async function checkVlBarcodes(
  ticketPlacementIds: string[],
  options?: { eventId?: string; persist?: boolean; logVerification?: boolean },
): Promise<CheckBarcodesResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/vl/checkBarcodes`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        ticketPlacementIds,
        ...(options?.eventId ? { eventId: options.eventId } : {}),
        ...(options?.persist != null ? { persist: options.persist } : {}),
        ...(options?.logVerification != null ? { logVerification: options.logVerification } : {}),
      },
    }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to check barcodes';
    throw new Error(message);
  }
  return responseData as CheckBarcodesResult;
}

export type BarcodeTeamSearchResult = {
  query: string;
  teams: Array<{
    team: string;
    platform: string;
    type: string;
    seasonsAccountLabel?: string | null;
    fallbackPurchase?: string | null;
  }>;
};

export async function searchBarcodeTeams(query: string): Promise<BarcodeTeamSearchResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/vl/searchBarcodeTeams`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input: { query } }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to search teams';
    throw new Error(message);
  }
  return responseData as BarcodeTeamSearchResult;
}

export type SeasonsAccountByTeamResult = {
  query: string;
  marketplaceName?: string | null;
  marketplaceId?: string | null;
  accountType?: string | null;
  accountId?: string | null;
  username?: string | null;
  error?: string | null;
};

export async function searchSeasonsAccountByTeam(
  teamName: string,
  options?: { marketplaceId?: string },
): Promise<SeasonsAccountByTeamResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/vl/searchSeasonsAccountByTeam`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        teamName,
        ...(options?.marketplaceId ? { marketplaceId: options.marketplaceId } : {}),
      },
    }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to search Seasons account';
    throw new Error(message);
  }
  return responseData as SeasonsAccountByTeamResult;
}

export type SeasonsMarketplaceOption = {
  teamName: string;
  marketplaceName: string;
  marketplaceId: string;
  accountType?: string | null;
};

export async function listSeasonsMarketplaces(): Promise<{
  marketplaces: SeasonsMarketplaceOption[];
}> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/vl/listSeasonsMarketplaces`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input: {} }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to load marketplaces';
    throw new Error(message);
  }
  return responseData as { marketplaces: SeasonsMarketplaceOption[] };
}

export type ListedEventByTeam = {
  eventId: string;
  eventName?: string | null;
  eventDate?: string | null;
  venue?: string | null;
  homeTeam?: string | null;
};

export async function searchListedEventsByTeam(teamName: string): Promise<{
  teamName: string;
  platform?: string | null;
  events: ListedEventByTeam[];
}> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/vl/searchListedEventsByTeam`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input: { teamName } }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to search listed events';
    throw new Error(message);
  }
  return responseData as { teamName: string; platform?: string | null; events: ListedEventByTeam[] };
}

export async function getEventBarcodeInventory(eventId: string): Promise<CheckBarcodesResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/vl/getEventBarcodeInventory`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input: { eventId } }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to load event inventory';
    throw new Error(message);
  }
  return responseData as CheckBarcodesResult;
}

export type SyncConciergeVlPricesResult = {
  successCount: number;
  failedCount: number;
  skippedCount?: number;
  unknownCount?: number;
  unchangedCount?: number;
  upCount?: number;
  downCount?: number;
  results?: {
    tpId: string;
    status: 'up' | 'down' | 'unchanged' | 'failed' | 'skipped' | 'unknown';
    message?: string;
    oldPrice?: number | null;
    newPrice?: number | null;
    direction?: 'up' | 'down' | 'unchanged' | 'failed' | 'skipped';
    section?: string | null;
    row?: string | null;
    seat?: string | number | null;
    eventName?: string | null;
    vlItemId?: string | null;
    vlListingId?: string | null;
  }[];
  recentChanges?: {
    lpId: string;
    tpId: string;
    oldPrice: number | null;
    newPrice: number | null;
    direction: 'up' | 'down' | 'unchanged';
    editType: string | null;
    createdAt: string | null;
    section?: string | null;
    row?: string | null;
    seat?: string | number | null;
    eventName?: string | null;
  }[];
  message?: string;
};

export async function syncConciergeVlPrices(
  ticketPlacementIds: string[],
  options?: { includeRecentMinutes?: number; includeAllHistory?: boolean },
): Promise<SyncConciergeVlPricesResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/syncConciergeVlPrices`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        ticketPlacementIds,
        ...(options?.includeRecentMinutes != null
          ? { includeRecentMinutes: options.includeRecentMinutes }
          : {}),
        includeAllHistory: options?.includeAllHistory !== false,
      },
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to sync concierge prices from Victory Live';
    throw new Error(message);
  }
  return responseData as SyncConciergeVlPricesResult;
}

export type ShareVlListingsResult = SyncVlListingStatusResult;

export const VL_MARKETPLACE_OPTIONS = [
  { id: 'stubhub', label: 'StubHub / Viagogo' },
  { id: 'seatgeek', label: 'SeatGeek' },
  { id: 'vivid', label: 'Vivid Seats' },
  { id: 'ticketnetwork', label: 'TicketNetwork' },
  { id: 'ticketevolution', label: 'Ticket Evolution' },
  { id: 'tickpick', label: 'TickPick' },
  { id: 'gametime', label: 'Gametime' },
  { id: 'ticketsnow', label: 'TicketsNow' },
  { id: 'fanx', label: 'FanXChange' },
  { id: 'axs', label: 'AXS' },
  { id: 'gotickets', label: 'GoTickets' },
] as const;

export async function shareVlListings(
  ticketPlacementIds: string[],
  options?: {
    shareExchange?: boolean;
    enabledMarketplaceIds?: string[];
    /** Save marketplaces only — updates VL NO* block codes without requiring pricing method. */
    updateMarketplaceNotes?: boolean;
    listPriceByTpId?: Record<string, number>;
    sectionByTpId?: Record<string, string>;
    rowByTpId?: Record<string, string>;
    seatByTpId?: Record<string, string>;
    internalNotes?: string;
    externalNotes?: string;
    pricingMethod?: 'MANUAL' | 'FOLLOW_MARKET' | 'NONE' | null;
    clearWrongPid?: boolean;
  },
): Promise<ShareVlListingsResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/shareVlListings`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      input: {
        ticketPlacementIds,
        shareExchange:
          options?.shareExchange === undefined ? true : Boolean(options.shareExchange),
        enabledMarketplaceIds: options?.enabledMarketplaceIds,
        updateMarketplaceNotes: options?.updateMarketplaceNotes === true,
        listPriceByTpId: options?.listPriceByTpId,
        sectionByTpId: options?.sectionByTpId,
        rowByTpId: options?.rowByTpId,
        seatByTpId: options?.seatByTpId,
        internalNotes: options?.internalNotes,
        externalNotes: options?.externalNotes,
        pricingMethod: options?.pricingMethod,
        ...(options?.clearWrongPid ? { clearWrongPid: true } : {}),
      },
    }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to share listings on Victory Live';
    throw new Error(message);
  }
  return responseData as ShareVlListingsResult;
}

export async function markWrongPid(ticketPlacementIds: string[]): Promise<SubmitListingsToVlResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/markWrongPid`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input: { ticketPlacementIds } }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to remove the wrong Production ID inventory';
    throw new Error(message);
  }
  return responseData as SubmitListingsToVlResult;
}

export async function searchVlProductions(input: {
  query?: string;
  eventName?: string;
  venue?: string;
  eventDate?: string;
  eventId?: string;
}): Promise<VlProductionSearchResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/searchVlProductions`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to search Victory Live productions';
    throw new Error(message);
  }
  return responseData as VlProductionSearchResult;
}

export async function mapVlProduction(input: {
  eventId: string;
  productionId: string;
}): Promise<MapVlProductionResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetch(`${NODE_API_URL}/api/mapVlProduction`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input }),
  });

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to map Victory Live production';
    throw new Error(message);
  }
  return responseData as MapVlProductionResult;
}

/** Step 2: QA-only status transition; this endpoint never calls Victory Live. */
export async function verifyTicketPlacements(
  ticketPlacementIds: string[],
): Promise<VerifyTicketPlacementsResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/verifyTicketPlacements`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input: { ticketPlacementIds } }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Could not verify ticket(s). Status unchanged. Please try again.',
    );
  }
  return responseData as VerifyTicketPlacementsResult;
}

/** Step 3: save the human-approved PID on the selected seats.
 *  Pass savePidOnly to write the PID without promoting ticket status or calling Victory Live. */
export async function confirmVlProduction(input: {
  eventId: string;
  productionId: string;
  ticketPlacementIds: string[];
  stubhubId?: string | null;
  seatgeekId?: string | null;
  /** DB-only: no status change, no VL submit (caller must not call submit either). */
  savePidOnly?: boolean;
}): Promise<ConfirmVlProductionResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/confirmVlProduction`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to confirm Victory Live production',
    );
  }
  return responseData as ConfirmVlProductionResult;
}

export type VlMarketplaceLinkResult = {
  productionId: string;
  stubhubId: string | null;
  seatgeekId: string | null;
};

/** Resolve StubHub / SeatGeek ids from VL catalog by production id (no DB columns). */
export async function resolveVlMarketplaceLinks(
  productionIds: string[],
): Promise<{ results: VlMarketplaceLinkResult[] }> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  const response = await fetch(`${NODE_API_URL}/api/resolveVlMarketplaceLinks`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ input: { productionIds } }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      responseData?.message ||
        responseData?.error ||
        responseData?.errors?.[0]?.message ||
        'Failed to resolve marketplace deep links',
    );
  }
  const raw =
    responseData?.results ||
    responseData?.data?.results ||
    responseData?.output?.results ||
    [];
  const results: VlMarketplaceLinkResult[] = (Array.isArray(raw) ? raw : []).map((row: any) => ({
    productionId: String(row?.productionId ?? row?.production_id ?? '').trim(),
    stubhubId:
      row?.stubhubId != null && String(row.stubhubId).trim() !== ''
        ? String(row.stubhubId).trim()
        : row?.stubhub_id != null && String(row.stubhub_id).trim() !== ''
          ? String(row.stubhub_id).trim()
          : null,
    seatgeekId:
      row?.seatgeekId != null && String(row.seatgeekId).trim() !== ''
        ? String(row.seatgeekId).trim()
        : row?.seatgeek_id != null && String(row.seatgeek_id).trim() !== ''
          ? String(row.seatgeek_id).trim()
          : null,
  })).filter((row) => row.productionId);
  return { results };
}

export type SplitVlListingsResult = SyncVlListingStatusResult & {
  sourceListingId?: string;
  newListingId?: string;
  splitQuantity?: number;
  status?: 'SUCCESS' | 'FAILURE';
  validation?: {
    ok: boolean;
    exceptions?: Array<{
      listingId: string | null;
      seat: string | number;
      errorCode: string;
      errorMessage: string;
      timestamp: string;
      tpId?: string;
    }>;
  };
};

export async function splitVlListings(
  ticketPlacementIds: string[],
  options?: { splitQuantity?: number },
): Promise<SplitVlListingsResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetchNoTimeout(
    `${NODE_API_URL}/api/splitVlListings`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        input: {
          ticketPlacementIds,
          ...(options?.splitQuantity != null
            ? { splitQuantity: options.splitQuantity }
            : {}),
        },
      }),
    },
  );

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to split listings on Victory Live';
    throw new Error(message);
  }
  return responseData as SplitVlListingsResult;
}

export type GroupVlListingsResult = SyncVlListingStatusResult & {
  mergedTicketGroupId?: string;
  sourceListingIds?: string[];
  status?: 'SUCCESS' | 'FAILURE';
  executionTimeMs?: number;
  validation?: {
    ok: boolean;
    exceptions?: Array<{
      listingId: string | null;
      seat: string | number;
      errorCode: string;
      errorMessage: string;
      timestamp: string;
      tpId?: string;
      originalListingIds?: string[];
      section?: string;
      row?: string;
      seatNumbers?: Array<string | number>;
    }>;
  };
};

export async function groupVlListings(
  ticketPlacementIds: string[],
): Promise<GroupVlListingsResult> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  const response = await fetchNoTimeout(
    `${NODE_API_URL}/api/groupVlListings`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        input: { ticketPlacementIds },
      }),
    },
  );

  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message ||
      responseData?.error ||
      responseData?.errors?.[0]?.message ||
      'Failed to start group listings on Victory Live';
    throw new Error(message);
  }
  return responseData as GroupVlListingsResult;
}

export type DelistTicketsResult = {
  status?: string;
  message?: string;
  successTpIds?: string[];
  failedTpIds?: string[];
  failedDetails?: Array<{
    tpId: string;
    failedStep?: 1 | 2 | 3 | null;
    error?: string;
  }>;
  jobId?: string;
  jobStatus?: string;
  currentStep?: 1 | 2 | 3 | null;
  completedSteps?: Array<1 | 2 | 3>;
  failedStep?: 1 | 2 | 3 | null;
};

export type DelistJobProgress = {
  currentStep?: 1 | 2 | 3 | null;
  completedSteps?: Array<1 | 2 | 3>;
  failedStep?: 1 | 2 | 3 | null;
  message?: string;
  jobStatus?: string;
};

export class DelistJobError extends Error {
  failedStep: 1 | 2 | 3 | null;
  completedSteps: Array<1 | 2 | 3>;
  currentStep: 1 | 2 | 3 | null;

  constructor(message: string, progress?: DelistJobProgress) {
    super(message);
    this.name = 'DelistJobError';
    this.failedStep = progress?.failedStep ?? null;
    this.completedSteps = progress?.completedSteps ?? [];
    this.currentStep = progress?.currentStep ?? progress?.failedStep ?? null;
  }
}

export type DelistTicketByAdminResult = {
  message?: string;
  jobId?: string;
  jobStatus?: string;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const readJobErrorMessage = (data: any, fallback: string): string =>
  data?.message || data?.error || data?.errors?.[0]?.message || fallback;

/** Short GETs stay under CloudFront's ~30s origin timeout; the POST itself returns 202 immediately. */
async function pollDelistJob<T>(
  jobId: string,
  fallback: string,
  onProgress?: (progress: DelistJobProgress) => void,
): Promise<T> {
  const started = Date.now();
  const timeoutMs = 8 * 60 * 1000;
  while (Date.now() - started < timeoutMs) {
    await sleep(1500);
    let response: Response;
    try {
      response = await fetch(
        `${NODE_API_URL}/api/delistTickets/jobs/${encodeURIComponent(jobId)}?t=${Date.now()}`,
        {
          method: 'GET',
          headers: {
            ...authHeaders(),
            'Cache-Control': 'no-cache',
          },
        },
      );
    } catch (err: any) {
      const msg = String(err?.message || err);
      if (/failed to fetch|networkerror|load failed/i.test(msg)) {
        throw new Error(
          'Delist is still running. Wait a moment and refresh — the ticket may already be Delisted.',
        );
      }
      throw err;
    }
    const data = await response.json().catch(() => ({}));
    const jobStatus = String(data?.jobStatus || '').toLowerCase();
    const progress: DelistJobProgress = {
      currentStep: data?.currentStep ?? null,
      completedSteps: Array.isArray(data?.completedSteps) ? data.completedSteps : [],
      failedStep: data?.failedStep ?? null,
      message: data?.message,
      jobStatus,
    };
    if (jobStatus === 'pending') {
      onProgress?.(progress);
      continue;
    }
    if (!response.ok) {
      throw new DelistJobError(readJobErrorMessage(data, fallback), progress);
    }
    if (jobStatus === 'failed') {
      onProgress?.(progress);
      throw new DelistJobError(readJobErrorMessage(data, fallback), progress);
    }
    onProgress?.({ ...progress, currentStep: 3, completedSteps: [1, 2, 3], failedStep: null });
    return data as T;
  }
  throw new Error('Delist is still running. Refresh to see if the ticket moved to Delisted.');
}

async function postDelistAndPoll<T>(
  url: string,
  body: unknown,
  fallback: string,
  onProgress?: (progress: DelistJobProgress) => void,
): Promise<T> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
  } catch (err: any) {
    const msg = String(err?.message || err);
    if (
      /failed to fetch|networkerror|load failed/i.test(msg) ||
      /timeout of \d+ms exceeded/i.test(msg) ||
      err?.name === 'AbortError'
    ) {
      throw new Error(
        'Delist is taking longer than expected. Wait a moment and refresh — the ticket may already be Delisted.',
      );
    }
    throw err;
  }

  const responseData = await response.json().catch(() => ({}));
  if (response.status === 202 && responseData?.jobId) {
    onProgress?.({
      currentStep: 1,
      completedSteps: [],
      failedStep: null,
      jobStatus: 'pending',
    });
    return pollDelistJob<T>(String(responseData.jobId), fallback, onProgress);
  }
  if (!response.ok) {
    throw new DelistJobError(readJobErrorMessage(responseData, fallback), {
      failedStep: 1,
      currentStep: 1,
      completedSteps: [],
      message: readJobErrorMessage(responseData, fallback),
    });
  }
  onProgress?.({ currentStep: 3, completedSteps: [1, 2, 3], failedStep: null, jobStatus: 'success' });
  return responseData as T;
}

export async function delistTicketsRequest(
  ticketPlacementIds: string[],
  requestedEmailId: string,
  onProgress?: (progress: DelistJobProgress) => void,
): Promise<DelistTicketsResult> {
  return postDelistAndPoll<DelistTicketsResult>(
    `${NODE_API_URL}/api/delistTickets?async=1`,
    {
      input: {
        delistTicketsInput: {
          ticketPlacementIds,
          requestedEmailId,
        },
      },
    },
    'Failed to request delist',
    onProgress,
  );
}

export async function delistTicketByAdminRequest(
  ticketPlacementId: string,
  onProgress?: (progress: DelistJobProgress) => void,
): Promise<DelistTicketByAdminResult> {
  return postDelistAndPoll<DelistTicketByAdminResult>(
    `${NODE_API_URL}/api/delistTicketBySuperUser?async=1`,
    {
      input: { ticketPlacementId },
    },
    'Failed to delist ticket',
    onProgress,
  );
}

export type { VlSupportEmailType } from '../vlSupportEmail';

export const VL_SUPPORT_MARKETPLACES = [
  'StubHub',
  'SeatGeek',
  'Ticketmaster',
  'Vivid Seats',
  'TickPick',
  'GameTime',
] as const;

export type SendVlSupportEmailResult = {
  success: boolean;
  sentTo?: string;
  redirectedForNonProd?: boolean;
  requestIds?: string[];
  warning?: string;
  ticketId?: string | null;
};

async function postVlSupport<T>(path: string, input: Record<string, unknown>, fallback: string): Promise<T> {
  if (!NODE_API_URL) {
    throw new Error('Backend API URL not configured (REACT_APP_NODE_API_URL)');
  }
  // Refresh an expired or missing token first so a tab left open for a while doesn't get "Unauthorized".
  const token = await getFreshIdToken();
  if (!token) throw new Error('Your session has expired. Please sign in again.');
  const response = await fetch(`${NODE_API_URL}/api/${path}`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ input }),
  });
  const responseData = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      responseData?.message || responseData?.error || responseData?.errors?.[0]?.message || fallback;
    throw new Error(
      response.status === 401 || /^unauthorized$/i.test(String(message))
        ? 'Your session has expired. Please sign in again.'
        : message,
    );
  }
  return responseData as T;
}

export type SeatMode = 'odd' | 'even';

/**
 * Limit a card to odd-only or even-only seats (null turns it off). Seats that don't fit are removed
 * from the listing, and "Odd / Even seating" goes into the internal and external notes. Call this
 * only from the dialog's Confirm click: the server refuses without `userConfirmed`.
 */
export function setSeatMode(tpIds: string[], mode: SeatMode | null) {
  return postVlSupport<{ mode: SeatMode | null; keptSeats: number[]; removedSeats: number[] }>(
    'setSeatMode',
    { tpIds, mode, userConfirmed: true },
    'Failed to change the seat numbers',
  );
}

export function sendVlSupportEmail(input: {
  tpIds: string[];
  type: VlSupportEmailType;
  marketplaces: string[];
  subject: string;
  body: string;
  /** Event details stored on the ticket for the Support Tickets page. */
  event?: { eventName: string; eventDate: string; pid: string; section: string; row: string; seats: string };
  /** Barcode requests: the account tried per event. */
  syncAccounts?: Array<VlSupportSyncAccount & { eventName: string }>;
  /** Who is in charge of the ticket: "Team" or one or more people's initials. */
  owners?: string[];
}): Promise<SendVlSupportEmailResult> {
  return postVlSupport<SendVlSupportEmailResult>(
    'sendVlSupportEmail',
    // Guardrail: the server refuses to send unless a person clicked Send. Only call this from that click handler.
    { ...input, userConfirmed: true },
    'Failed to send email to Victory Live',
  );
}

export function completeVlSupportRequest(tpIds: string[]): Promise<{ success: boolean; updatedCount: number }> {
  return postVlSupport<{ success: boolean; updatedCount: number }>(
    'completeVlSupportRequest',
    { tpIds },
    'Failed to mark Victory Live support request completed',
  );
}

/** Only call from the Reply button's click handler — the server refuses sends without userConfirmed. */
export function replyVlSupportTicket(ticketId: string, body: string, owners?: string[]) {
  return postVlSupport<{ success: boolean; sentTo?: string; redirectedForNonProd?: boolean; owners?: string[] }>(
    'vlSupport/reply',
    { ticketId, body, owners, userConfirmed: true },
    'Failed to send reply to Victory Live',
  );
}

/** Link a ticket (usually one Victory Live started) to a user and/or an event. Use null to remove a link. Sends no email. */
export function linkVlSupportTicket(ticketId: string, links: { userId?: string | null; eventId?: string | null }) {
  return postVlSupport<{ success: boolean; userId?: string; eventId?: string }>(
    'vlSupport/link',
    { ticketId, ...links },
    'Failed to link the ticket',
  );
}

/** Replace who is in charge of a ticket. Sends no email. */
export function setVlSupportTicketOwners(ticketId: string, owners: string[]) {
  return postVlSupport<{ success: boolean; owners: string[] }>(
    'vlSupport/setOwners',
    { ticketId, owners },
    'Failed to update who is in charge',
  );
}

export function completeVlSupportTicket(ticketId: string) {
  return postVlSupport<{ success: boolean; secondsOpen?: number }>(
    'vlSupport/complete',
    { ticketId },
    'Failed to mark the support ticket complete',
  );
}

/** Mark many tickets complete at once. Returns how many were completed and which failed. */
export function completeVlSupportTickets(ticketIds: string[]) {
  return postVlSupport<{ success: boolean; completed: number; alreadyCompleted: number; failed: string[] }>(
    'vlSupport/completeMany',
    { ticketIds },
    'Failed to mark the support tickets complete',
  );
}

export function reopenVlSupportTicket(ticketId: string) {
  return postVlSupport<{ success: boolean }>('vlSupport/reopen', { ticketId }, 'Failed to reopen the support ticket');
}

export function markVlSupportTicketRead(ticketId: string) {
  return postVlSupport<{ success: boolean }>('vlSupport/markRead', { ticketId }, 'Failed to mark the ticket read');
}

export type VlSupportInsights = {
  from: string;
  to: string;
  listingsCount: number;
  byType: Array<{
    type: 'barcode_sync' | 'not_populating' | 'other' | 'need_pid' | 'inbound';
    ticketCount: number;
    openCount: number;
    completedCount: number;
    avgSecondsToComplete: number | null;
    medianSecondsToComplete: number | null;
    listingsContacted: number;
    listingsTotal?: number;
    contactRate: number | null;
  }>;
  marketplaces: Array<{ marketplace: string; count: number }>;
  /** Barcode requests grouped by the account we tried (e.g. "Season Seat Manager · Ticketmaster"). */
  barcodeAccounts?: Array<{
    group: string;
    count: number;
    openCount: number;
    completedCount: number;
    avgSecondsToComplete: number | null;
  }>;
  owners: Array<{ owner: string; count: number }>;
  /** Barcode help: requests by account tried and the share of listings that needed it (optionally one team). */
  barcode?: {
    team: string | null;
    ticketCount: number;
    listingsContacted: number;
    listingsTotal: number;
    listingRate: number | null;
    groups: Array<{ group: string; count: number; openCount: number; avgSecondsToComplete: number | null }>;
  };
  /** Teams on the Season Seat Manager sheet with their league, for the league -> team filters. */
  syncTeams?: Array<{ team: string; league: string }>;
  tickets: Array<{
    ticketId: string;
    type: 'barcode_sync' | 'not_populating' | 'other' | 'need_pid' | 'inbound';
    status: 'open' | 'completed';
    eventName: string | null;
    eventDate: string | null;
    pid: string | null;
    ownerInitials: string | null;
    owners?: string[];
    openedAt: string;
    reopenCount: number;
    secondsOpen: number;
    syncAccounts?: Array<{ method: 'season_seat_manager' | 'purchase_manager'; team?: string | null; platform: string | null }>;
  }>;
};

export function getVlSupportInsights(range: { from?: string; to?: string; team?: string | null; userId?: string | null }) {
  return postVlSupport<VlSupportInsights>('vlSupport/insights', range, 'Failed to load support insights');
}

export type VlSupportInboxStatus = {
  configured: boolean;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastCapturedAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  /** False while the backend is restoring the support tables in Hasura. */
  trackingAvailable?: boolean | null;
};

export function getVlSupportInboxStatus() {
  return postVlSupport<VlSupportInboxStatus>('vlSupport/inboxStatus', {}, 'Failed to read the support inbox status');
}

export type VlSupportSyncAccount = {
  method: 'season_seat_manager' | 'purchase_manager';
  team: string | null;
  platform: string | null;
};

export type VlSupportEventSyncOptions = {
  eventName: string;
  selected: VlSupportSyncAccount;
  options: VlSupportSyncAccount[];
};

/** Barcode requests: which account to say we tried, per event, and what can be picked instead. */
export function getVlSupportBarcodeSyncOptions(tpIds: string[]) {
  return postVlSupport<{ events: VlSupportEventSyncOptions[] }>(
    'vlSupport/barcodeSyncOptions',
    { tpIds },
    'Failed to look up the sync account',
  );
}
